// Client portal — read-only restricted view for clients (Lori, Keith, future
// owners). Sees: schedule progress, photos, selections, change orders.
// Hidden: subs, sub bids, Heritage margin/GC fee, daily logs, audit log,
// internal punch list, draws, scope items.

import { useEffect, useMemo, useState } from 'react'
import { api } from './api/client'
import type {
  ChangeOrder,
  Decision,
  Me,
  Project,
  ScheduleTask,
  Selection,
  SiteWalk,
  WarrantyClaim,
} from './types'
import { addBusinessDays, fmtDate, fmtDateShort, fmtMoney } from './lib/format'
import Badge from './components/Badge'
import ProgressBar from './components/ProgressBar'

export default function ClientApp({ me }: { me: Me }) {
  const [project, setProject] = useState<Project | null>(null)
  const [tab, setTab] = useState<'progress' | 'selections' | 'changes' | 'photos' | 'warranty'>('progress')

  useEffect(() => {
    if (me.client_projects.length === 0) return
    void api.projects.get(me.client_projects[0].id).then(setProject)
  }, [me])

  if (me.client_projects.length === 0) {
    return (
      <main className="shell">
        <h1>Heritage Command Center</h1>
        <p className="muted">
          You're logged in as {me.email}, but no project has granted you client access. Ask Dustin
          to add your email to the project's client list.
        </p>
      </main>
    )
  }

  if (!project) {
    return (
      <main className="shell">
        <p className="muted">Loading…</p>
      </main>
    )
  }

  return (
    <>
      <header className="hdr">
        <div className="brand">
          <img src="/logo.png" alt="Heritage Homes" className="brand-logo" />
          <div className="brand-text">
            <div className="brand-sub">CLIENT PORTAL</div>
          </div>
        </div>
        <span className="muted small">{project.name}</span>
        <nav className="tabs">
          <button className={`tab-btn ${tab === 'progress' ? 'active' : ''}`} onClick={() => setTab('progress')}>
            Progress
          </button>
          <button className={`tab-btn ${tab === 'selections' ? 'active' : ''}`} onClick={() => setTab('selections')}>
            Selections
          </button>
          <button className={`tab-btn ${tab === 'changes' ? 'active' : ''}`} onClick={() => setTab('changes')}>
            Changes
          </button>
          <button className={`tab-btn ${tab === 'photos' ? 'active' : ''}`} onClick={() => setTab('photos')}>
            Photos
          </button>
          <button className={`tab-btn ${tab === 'warranty' ? 'active' : ''}`} onClick={() => setTab('warranty')}>
            Warranty
          </button>
        </nav>
      </header>
      <main className="main">
        {tab === 'progress' && <Progress project={project} />}
        {tab === 'selections' && <Selections project={project} />}
        {tab === 'changes' && <Changes project={project} />}
        {tab === 'photos' && <Photos project={project} />}
        {tab === 'warranty' && <Warranty project={project} me={me} />}
      </main>
    </>
  )
}

function Progress({ project }: { project: Project }) {
  const [tasks, setTasks] = useState<ScheduleTask[]>([])
  const [decisions, setDecisions] = useState<Decision[]>([])

  useEffect(() => {
    void api.schedule.list(project.id).then(setTasks)
    void api.decisions.list(project.id).then(setDecisions)
  }, [project.id])

  const completed = tasks.filter((t) => t.completed).length
  const phases = useMemo(() => {
    const map = new Map<string, ScheduleTask[]>()
    for (const t of tasks) {
      const arr = map.get(t.phase) ?? []
      arr.push(t)
      map.set(t.phase, arr)
    }
    return [...map.entries()]
  }, [tasks])

  const nextPhase = phases.find(([, items]) => items.some((t) => !t.completed))?.[0] ?? '—'

  // Compute phase date ranges if start_date is set
  const phaseDates = useMemo(() => {
    if (!project.start_date) return new Map<string, { start: Date; end: Date }>()
    const result = new Map<string, { start: Date; end: Date }>()
    let cursor = new Date(project.start_date)
    for (const [phase, items] of phases) {
      const start = new Date(cursor)
      for (const t of items) cursor = addBusinessDays(cursor, t.duration_business_days)
      result.set(phase, { start, end: new Date(cursor) })
    }
    return result
  }, [phases, project.start_date])

  return (
    <section>
      <h2>{project.name}</h2>
      <p className="muted small">
        {project.address ?? ''}
        {project.city ? `, ${project.city}` : ''}
      </p>

      <div className="stat-grid mb-4">
        <div className="stat-card">
          <div className="stat-label">Status</div>
          <div className="stat-value">{project.status.replace('-', ' ')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Progress</div>
          <div className="stat-value">{tasks.length ? Math.round((completed / tasks.length) * 100) : 0}%</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Currently in</div>
          <div className="stat-value" style={{ fontSize: 16 }}>{nextPhase}</div>
        </div>
      </div>

      <div className="card mb-3">
        <ProgressBar pct={tasks.length ? (completed / tasks.length) * 100 : 0} />
        <p className="muted small mt-2">
          {completed} of {tasks.length} tasks complete
        </p>
      </div>

      <h3>Phases</h3>
      {phases.map(([phase, items]) => {
        const phaseDone = items.every((t) => t.completed)
        const phaseStarted = items.some((t) => t.completed)
        const dates = phaseDates.get(phase)
        return (
          <div key={phase} className="phase-box">
            <div className="phase-hdr">
              <span className="phase-name">
                {phaseDone ? '✓ ' : phaseStarted ? '▶ ' : ''}
                {phase}
              </span>
              <span className="phase-date">
                {dates ? `${fmtDateShort(dates.start.toISOString())} → ${fmtDateShort(dates.end.toISOString())}` : ''}
              </span>
            </div>
          </div>
        )
      })}

      <h3 className="mt-4">Decisions you need to make</h3>
      {decisions.filter((d) => !d.completed).length === 0 ? (
        <p className="muted">All decisions resolved.</p>
      ) : (
        <div className="card">
          {decisions
            .filter((d) => !d.completed)
            .map((d) => (
              <div key={d.id} className="row-list" style={{ cursor: 'default' }}>
                <div className="grow">
                  <div className="row-title">{d.decision_name}</div>
                  <div className="muted small">
                    Needed for: {d.phase_needed_for ?? '—'} · {d.lead_weeks ?? '—'}wk lead
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </section>
  )
}

function Selections({ project }: { project: Project }) {
  const [items, setItems] = useState<Selection[]>([])
  useEffect(() => {
    void api.selections.list(project.id).then(setItems)
  }, [project.id])

  const totals = {
    allowance: items.reduce((s, i) => s + i.allowance_amount, 0),
    actual: items.reduce((s, i) => s + (i.actual_amount ?? 0), 0),
  }

  return (
    <section>
      <h2>Selections</h2>
      <p className="muted small mb-3">{items.length} items · what's been picked, what's pending.</p>
      <div className="stat-grid mb-3">
        <div className="stat-card">
          <div className="stat-label">Allowance budget</div>
          <div className="stat-value">{fmtMoney(totals.allowance)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Decided so far</div>
          <div className="stat-value">{fmtMoney(totals.actual)}</div>
        </div>
      </div>
      <div className="card no-pad">
        {items.map((s) => (
          <div key={s.id} className="row-list" style={{ cursor: 'default' }}>
            <div className="grow">
              <div className="row-title">{s.category}</div>
              <div className="muted small">
                {s.item_description ?? '—'} · allowance {fmtMoney(s.allowance_amount)}
                {s.actual_amount !== null && ` · picked ${fmtMoney(s.actual_amount)}`}
              </div>
            </div>
            <Badge color={s.status === 'installed' ? 'green' : s.status === 'ordered' ? 'green' : s.status === 'decided' ? 'gold' : 'gray'}>
              {s.status.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
        ))}
      </div>
    </section>
  )
}

function Changes({ project }: { project: Project }) {
  const [cos, setCos] = useState<ChangeOrder[]>([])
  useEffect(() => {
    void api.changeOrders.list(project.id).then(setCos)
  }, [project.id])

  const total = cos.filter((c) => c.status === 'approved').reduce((s, c) => s + c.cost_impact, 0)

  return (
    <section>
      <h2>Change Orders</h2>
      <p className="muted small mb-3">
        {cos.length} CO{cos.length === 1 ? '' : 's'} · approved impact {fmtMoney(total)}
      </p>
      {cos.map((co) => (
        <div key={co.id} className="card mb-2">
          <div className="row-between">
            <div>
              <div className="row-title">CO-{String(co.co_number).padStart(2, '0')} · {co.description}</div>
              <div className="muted small">
                {fmtDate(co.date)} · cost {fmtMoney(co.cost_impact)} · {co.schedule_impact_days}d schedule
              </div>
            </div>
            <Badge color={co.status === 'approved' ? 'green' : co.status === 'rejected' ? 'gray' : 'gold'}>
              {co.status.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
        </div>
      ))}
    </section>
  )
}

function Photos({ project }: { project: Project }) {
  const [walks, setWalks] = useState<SiteWalk[]>([])
  useEffect(() => {
    void api.siteWalks.list(project.id).then(setWalks)
  }, [project.id])

  // Flatten all photos across walks, newest first
  const allPhotos = useMemo(() => {
    const result: Array<{ key: string; date: string; phase: string | null }> = []
    for (const w of walks) {
      let keys: string[] = []
      try {
        keys = w.photo_urls ? (JSON.parse(w.photo_urls) as string[]) : []
      } catch {
        keys = []
      }
      for (const k of keys) result.push({ key: k, date: w.date, phase: w.phase })
    }
    return result
  }, [walks])

  return (
    <section>
      <h2>Build photos</h2>
      <p className="muted small mb-3">{allPhotos.length} photos across {walks.length} site walks.</p>
      {allPhotos.length === 0 ? (
        <p className="muted">No photos yet — they'll appear as Dustin captures site walks.</p>
      ) : (
        <div className="photo-grid">
          {allPhotos.map((p, i) => (
            <a key={i} href={api.fileUrl(p.key)} target="_blank" rel="noopener noreferrer">
              <img className="photo-thumb" src={api.fileUrl(p.key)} alt={`${p.phase} ${p.date}`} loading="lazy" />
            </a>
          ))}
        </div>
      )}
    </section>
  )
}


// ----- Warranty (client view) ----------------------------------------------

function Warranty({ project, me }: { project: Project; me: Me }) {
  const [claims, setClaims] = useState<WarrantyClaim[]>([])
  const [filing, setFiling] = useState(false)
  const [draft, setDraft] = useState<{
    title: string
    description: string
    location: string
    is_emergency: boolean
  }>({ title: '', description: '', location: '', is_emergency: false })
  const [photoFiles, setPhotoFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    void api.warranty.list(project.id).then((all) => {
      // Client only sees their own claims
      setClaims(all.filter((c) => c.submitted_by.toLowerCase() === me.email.toLowerCase()))
    })
  }, [project.id, me.email])

  async function submit() {
    if (!draft.title || !draft.description) {
      setErr('Title and description required')
      return
    }
    setSubmitting(true)
    setErr(null)
    try {
      const claim = await api.warranty.create(project.id, {
        title: draft.title,
        description: draft.description,
        location: draft.location || null,
        is_emergency: draft.is_emergency ? 1 : 0,
        submitted_by_name: me.email,
      })
      // Upload photos if any
      if (photoFiles.length > 0) {
        await api.warranty.uploadPhotos(claim.id, photoFiles)
      }
      // Refresh
      const fresh = await api.warranty.list(project.id)
      setClaims(fresh.filter((c) => c.submitted_by.toLowerCase() === me.email.toLowerCase()))
      setFiling(false)
      setDraft({ title: '', description: '', location: '', is_emergency: false })
      setPhotoFiles([])
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Warranty Claims</h2>
          <p className="muted small">
            File a warranty claim for issues with the home. Heritage acknowledges within 24 hrs and
            dispatches the responsible sub. {claims.length} of yours on record.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setFiling(!filing)}>
          {filing ? 'Cancel' : '+ File a claim'}
        </button>
      </div>

      {filing && (
        <div className="card mb-3">
          <h3>File a warranty claim</h3>
          <p className="muted small mb-2">
            Coverage: 1yr workmanship & materials · 2yr plumbing/electrical/HVAC · 10yr structural
            (per MN 327A.02). Maintenance items aren't covered but Heritage will guide.
          </p>

          {err && <div className="card alert mb-2">{err}</div>}

          <label className="form-row">
            <span>Short title *</span>
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Master shower drain leaking"
              autoFocus
            />
          </label>
          <label className="form-row">
            <span>Location</span>
            <input
              value={draft.location}
              onChange={(e) => setDraft({ ...draft, location: e.target.value })}
              placeholder="Master bathroom"
            />
          </label>
          <label className="form-row">
            <span>Description *</span>
            <textarea
              rows={4}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="When does it happen, what does it look like, anything you've already tried…"
            />
          </label>
          <label className="form-row">
            <span>Photos</span>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setPhotoFiles(Array.from(e.target.files ?? []))}
            />
          </label>
          <label className="form-row">
            <span>Emergency?</span>
            <span className="row gap-2">
              <input
                type="checkbox"
                checked={draft.is_emergency}
                onChange={(e) => setDraft({ ...draft, is_emergency: e.target.checked })}
              />
              <span className="muted small">
                Active water leak, no heat, no electrical, anything dangerous. Same-day response.
              </span>
            </span>
          </label>
          <div className="row gap-2 mt-2">
            <button className="btn-primary" onClick={submit} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit claim'}
            </button>
            <button className="btn-ghost" onClick={() => setFiling(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {claims.length === 0 && !filing && (
        <p className="muted">No claims filed yet.</p>
      )}

      {claims.map((c) => (
        <div key={c.id} className="card mb-2">
          <div className="row-between">
            <div className="grow">
              <div className="row-title">
                {c.is_emergency === 1 && <span className="danger">⚠ EMERGENCY · </span>}
                {c.title}
              </div>
              <div className="muted small">
                Filed {fmtDate(c.submitted_at)}
                {c.location && ` · ${c.location}`}
              </div>
              <div className="muted small mt-1">{c.description.slice(0, 200)}{c.description.length > 200 && '…'}</div>
              {c.responsible_sub_name && (
                <div className="muted small mt-1">Assigned to: {c.responsible_sub_name}</div>
              )}
            </div>
            <Badge
              color={
                c.status === 'closed' || c.status === 'resolved'
                  ? 'green'
                  : c.status === 'rejected'
                    ? 'gray'
                    : 'gold'
              }
            >
              {c.status.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
        </div>
      ))}
    </section>
  )
}
